import {
  computed,
  onScopeDispose,
  ref,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
} from 'vue'

import { insightApi } from '../api/InsightApi'
import type { InsightResult } from '../models/insight.types'
import type { SummaryInsightItem, SummaryInsightsState } from '../models/summaryInsight.types'
import { toSummaryInsightItem } from '../service/insightPresentation'
import { useInsightStore, type InsightStore } from '../stores/insightStore'

import { useAccount, useWorkspace } from '@/features/account'
import { ApiError, type Paginated } from '@/shared/http'

interface SummaryInsightsApi {
  list: (
    accountId: number,
    workspaceId: number,
    params: { limit: number },
    signal?: AbortSignal,
  ) => Promise<Paginated<InsightResult>>
}

export interface UseSummaryInsightsOptions {
  accountId?: MaybeRefOrGetter<number | undefined>
  workspaceId?: MaybeRefOrGetter<string | number | undefined>
  api?: SummaryInsightsApi
  now?: () => number
  limit?: number
  store?: InsightStore
}

export interface UseSummaryInsights {
  insights: ComputedRef<readonly SummaryInsightItem[]>
  total: ComputedRef<number>
  state: ComputedRef<SummaryInsightsState>
  error: ComputedRef<ApiError | null>
  /** `force` bypasses the workspace cache — a workspace change already does this on its own. */
  load: (force?: boolean) => Promise<void>
}

export function useSummaryInsights(options: UseSummaryInsightsOptions = {}): UseSummaryInsights {
  const account = options.accountId ? null : useAccount()
  const workspace = options.workspaceId ? null : useWorkspace()
  const api = options.api ?? insightApi
  const store = options.store ?? useInsightStore()
  const items = ref<SummaryInsightItem[]>([])
  const total = ref(0)
  const state = ref<SummaryInsightsState>('loading')
  const error = ref<ApiError | null>(null)
  let controller: AbortController | null = null

  const context = computed(() => ({
    accountId: options.accountId ? toValue(options.accountId) : account?.accountId.value,
    workspaceId: options.workspaceId ? toValue(options.workspaceId) : workspace?.workspaceId.value,
  }))

  async function load(force = false): Promise<void> {
    const { accountId, workspaceId } = context.value
    const numericWorkspaceId = Number(workspaceId)
    if (
      accountId === undefined ||
      workspaceId === undefined ||
      !Number.isFinite(numericWorkspaceId)
    ) {
      controller?.abort()
      items.value = []
      total.value = 0
      error.value = null
      state.value = 'empty'
      return
    }

    if (!force && store.hasFreshList(numericWorkspaceId)) {
      const limit = options.limit ?? 3
      items.value = store.topN(limit).map((item) => toSummaryInsightItem(item, options.now?.()))
      total.value = store.count
      state.value = items.value.length > 0 ? 'ready' : 'empty'
      return
    }

    controller?.abort()
    store.ensureWorkspace(numericWorkspaceId)
    const request = new AbortController()
    controller = request
    state.value = 'loading'
    error.value = null
    try {
      const limit = options.limit ?? 3
      const page = await api.list(accountId, numericWorkspaceId, { limit }, request.signal)
      if (request.signal.aborted) return
      if (!store.setList(numericWorkspaceId, page.results, page.count)) return
      items.value = store.topN(limit).map((item) => toSummaryInsightItem(item, options.now?.()))
      total.value = page.count
      state.value = items.value.length > 0 ? 'ready' : 'empty'
    } catch (cause) {
      const normalized = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      if (normalized.kind === 'canceled' || request.signal.aborted) return
      items.value = []
      total.value = 0
      error.value = normalized
      state.value = 'error'
    }
  }

  watch(
    context,
    () => {
      void load()
    },
    { immediate: true },
  )
  onScopeDispose(() => controller?.abort())

  return {
    insights: computed(() => items.value),
    total: computed(() => total.value),
    state: computed(() => state.value),
    error: computed(() => error.value),
    load,
  }
}
